import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Carousel from 'react-multi-carousel';
import { formatNumberCurrency } from '@panel/commons/helpers/formatAmount';
import { sendCustomTrackingInfo } from '@panel/commons';
import 'react-multi-carousel/lib/styles.css';
import { PlansSelectors, PlansOperations } from '../../../ducks/plans';
import { getSiteCurrency } from '../../../helpers/getPlansInformation';
import { Container, HeaderContainer, Title, Subtitle, CarouselContainer } from './styles';
import PlanCard from '../../PlanCard';

const site = process.env.REACT_APP_BUILD_SITE;
const PlansSection = ({ device, plansRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const recommendedPlan = 0;
  const positionRef = useRef(null);
  const { getPlansInfo } = PlansOperations;
  const { plansInfo, errorPlans } = useSelector(PlansSelectors.getPlansState);

  const responsive = {
    desktop: {
      breakpoint: {
        max: 3000,
        min: 1081
      },
      items: 2,
      partialVisibilityGutter: 0
    },
    tablet: {
      breakpoint: {
        max: 1081,
        min: 464
      },
      items: 2,
      partialVisibilityGutter: 0
    },
    mobile: {
      breakpoint: {
        max: 464,
        min: 0
      },
      items: 1,
      partialVisibilityGutter: 100
    }
  };

  useEffect(() => {
    dispatch(getPlansInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Se implementa efecto para disparar evento de data layers cuándo las cards se visualizan en pantalla.
    const elemento = positionRef.current;
    const observer = new window.IntersectionObserver(
      entries => {
        const { isIntersecting } = entries[0];
        // Accedemos a la propiedad isIntersecting que cambia a true cuando el elemento se visualiza dentro del viewport
        if (isIntersecting) {
          sendCustomTrackingInfo({
            event: 'productImpression',
            ecommerce: {
              currencyCode: getSiteCurrency[site],
              flow_type: 'Profesional',
              impressions:
                plansInfo &&
                plansInfo.length > 0 &&
                plansInfo.map(plan => {
                  return {
                    id: plan.id,
                    name: plan.name.replace('1 ', ''),
                    price: formatNumberCurrency(plan.unitPrice, getSiteCurrency[site]),
                    category: 'Aviso Profesionales',
                    position: plan?.position + 1,
                    list: 'Listado landing Zona Demand'
                  };
                })
            }
          });
          // Acá desactivamos el observer una vez que ya se mostró el elemento en el viewport y se actualizó el isIntersecting en true:
          observer.disconnect();
        }
      },
      // El evento se disparará al visualizar el elemento al 80%:
      { threshold: 0.8 }
    );
    if (elemento && plansInfo) {
      // Esto va a monitorear cada vez que se muestra un elemento dentro del viewport
      observer.observe(elemento);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansInfo]);

  return (
    <Container device={device} data-panel='landingZD-plansSection' site={site} ref={plansRef}>
      <HeaderContainer device={device}>
        <Title device={device} data-panel='landingZD-plansSection-title'>
          {t('panel.landing_ZD.PlansSection.titleSection')}
        </Title>
        <Subtitle device={device} data-panel='landingZD-plansSection-title'>
          {t('panel.landing_ZD.PlansSection.subtitleSection')}
        </Subtitle>
      </HeaderContainer>
      <CarouselContainer
        device={device}
        site={site}
        data-panel='landingZD-plansSection-carousel-container'
        ref={positionRef}
      >
        {!errorPlans && plansInfo && plansInfo.length > 0 && (
          <Carousel
            responsive={responsive}
            swipeable
            draggable={false}
            showDots={false}
            renderDotsOutside={false}
            shouldResetAutoplay={false}
            minimumTouchDrag={50}
            partialVisible
            arrows={false}
            slidesToSlide={1}
          >
            {plansInfo.map(plan => {
              return (
                <PlanCard
                  device={device}
                  site={site}
                  key={plan.id}
                  idProducto={plan.id}
                  nameCard={plan.name}
                  priceCard={plan.unitPrice}
                  // ON-9639
                  priceCardWithTaxes={plan.unitPriceWithTaxes}
                  currency={getSiteCurrency[site]}
                  postPrice={plan.renewalFrequency}
                  description={plan.description}
                  note={plan.noteRenewal}
                  noteTaxes={plan.noteTaxes}
                  type={plan.type}
                  tag={plan.position === recommendedPlan}
                  position={plan.position}
                />
              );
            })}
          </Carousel>
        )}
      </CarouselContainer>
    </Container>
  );
};

export default PlansSection;
